import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Divider,
  Card,
  CardContent,
  Tooltip,
  LinearProgress,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Warning as WarningIcon,
  Sms as SmsIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  History as HistoryIcon,
  People as PeopleIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  Summarize as SummarizeIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { adminAPI } from '../services/api';

// Flag metadata for display purposes
const FLAG_CONFIG = {
  ingestion_enabled: {
    label: 'Global Ingestion',
    description: 'Master kill switch - disables ALL message processing across all channels',
    icon: <ToggleOnIcon />,
    color: 'error',
    critical: true,
  },
  sms_enabled: {
    label: 'SMS Channel',
    description: 'Enable/disable SMS message processing',
    icon: <SmsIcon />,
    color: 'primary',
  },
  voice_enabled: {
    label: 'Voice Channel',
    description: 'Enable/disable voice call processing',
    icon: <PhoneIcon />,
    color: 'primary',
  },
  email_enabled: {
    label: 'Email Channel',
    description: 'Enable/disable email message processing',
    icon: <EmailIcon />,
    color: 'primary',
  },
  new_user_registration_enabled: {
    label: 'New User Registration',
    description: 'Allow new users to sign up',
    icon: <PersonAddIcon />,
    color: 'secondary',
  },
  training_enabled: {
    label: 'Training System',
    description: 'Enable/disable all training exercises system-wide (SMS, practice, scheduled)',
    icon: <SchoolIcon />,
    color: 'warning',
  },
};

// Limit metadata for display purposes
const LIMIT_CONFIG = {
  beta_user_limit: {
    label: 'Beta User Cap',
    description: 'Maximum number of users allowed during beta (0 = unlimited)',
    icon: <PeopleIcon />,
    color: 'secondary',
    unit: 'users',
  },
  user_daily_sms_limit: {
    label: 'Per-User Daily SMS',
    description: 'Max SMS submissions per user per day (0 = unlimited)',
    icon: <SmsIcon />,
    color: 'primary',
    unit: 'per user/day',
  },
  user_daily_voice_limit: {
    label: 'Per-User Daily Voice',
    description: 'Max voice calls per user per day (0 = unlimited)',
    icon: <PhoneIcon />,
    color: 'primary',
    unit: 'per user/day',
  },
  user_daily_email_limit: {
    label: 'Per-User Daily Email',
    description: 'Max email submissions per user per day (0 = unlimited)',
    icon: <EmailIcon />,
    color: 'primary',
    unit: 'per user/day',
  },
  global_daily_sms_limit: {
    label: 'Global Daily SMS',
    description: 'Max total SMS per day across all users (0 = unlimited)',
    icon: <SmsIcon />,
    color: 'warning',
    unit: 'total/day',
  },
  global_daily_voice_limit: {
    label: 'Global Daily Voice',
    description: 'Max total voice calls per day across all users (0 = unlimited)',
    icon: <PhoneIcon />,
    color: 'warning',
    unit: 'total/day',
  },
  global_daily_email_limit: {
    label: 'Global Daily Email',
    description: 'Max total emails per day across all users (0 = unlimited)',
    icon: <EmailIcon />,
    color: 'warning',
    unit: 'total/day',
  },
};

const FlagCard = ({ flag, config, onToggle, loading }) => {
  const isEnabled = flag.value;
  
  return (
    <Card 
      sx={{ 
        mb: 2,
        borderLeft: config?.critical ? '4px solid' : 'none',
        borderColor: config?.critical ? (isEnabled ? 'success.main' : 'error.main') : 'transparent',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
            <Box
              sx={{
                bgcolor: isEnabled ? `${config?.color || 'primary'}.light` : 'grey.200',
                color: isEnabled ? `${config?.color || 'primary'}.main` : 'grey.500',
                p: 1.5,
                borderRadius: 2,
                display: 'flex',
              }}
            >
              {config?.icon || <ToggleOnIcon />}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={500} sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                  {config?.label || flag.key}
                </Typography>
                {config?.critical && (
                  <Chip label="Critical" size="small" color="error" variant="outlined" />
                )}
                <Chip
                  label={isEnabled ? 'Enabled' : 'Disabled'}
                  size="small"
                  color={isEnabled ? 'success' : 'default'}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {config?.description || flag.description || 'No description available'}
              </Typography>
              {flag.updatedAt && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <HistoryIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.disabled">
                    Last changed: {new Date(flag.updatedAt).toLocaleString()}
                    {flag.updatedBy && ` by ${flag.updatedBy}`}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Tooltip title={loading ? 'Updating...' : (isEnabled ? 'Click to disable' : 'Click to enable')}>
            <span>
              <Switch
                checked={isEnabled}
                onChange={() => onToggle(flag.key, !isEnabled)}
                disabled={loading}
                color={config?.color || 'primary'}
              />
            </span>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

const LimitCard = ({ limit, config, currentUsage, onUpdate, loading }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(limit.value);
  
  const usagePercent = limit.value > 0 && currentUsage !== undefined
    ? Math.min((currentUsage / limit.value) * 100, 100)
    : 0;
  
  const handleSave = () => {
    const numValue = parseInt(editValue, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      onUpdate(limit.key, numValue);
      setEditing(false);
    }
  };
  
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              bgcolor: `${config?.color || 'primary'}.light`,
              color: `${config?.color || 'primary'}.main`,
              p: 1.5,
              borderRadius: 2,
              display: 'flex',
            }}
          >
            {config?.icon || <SpeedIcon />}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6" fontWeight={500} sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}>
                {config?.label || limit.key}
              </Typography>
              {editing ? (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    type="number"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    sx={{ width: 100 }}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">
                        <Typography variant="caption" color="text.secondary">
                          {config?.unit?.split('/')[0] || ''}
                        </Typography>
                      </InputAdornment>,
                    }}
                  />
                  <Button size="small" onClick={handleSave} disabled={loading}>
                    Save
                  </Button>
                  <Button size="small" onClick={() => { setEditing(false); setEditValue(limit.value); }}>
                    Cancel
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip 
                    label={limit.value === 0 ? 'Unlimited' : `${limit.value} ${config?.unit || ''}`}
                    size="small"
                    color={limit.value === 0 ? 'default' : 'primary'}
                    variant="outlined"
                  />
                  <Button size="small" onClick={() => setEditing(true)}>
                    Edit
                  </Button>
                </Box>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {config?.description || limit.description || 'No description available'}
            </Typography>
            
            {/* Usage progress bar for global limits */}
            {limit.key.startsWith('global_daily') && currentUsage !== undefined && limit.value > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Today's usage
                  </Typography>
                  <Typography variant="caption" fontWeight={500}>
                    {currentUsage} / {limit.value}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={usagePercent}
                  color={usagePercent > 90 ? 'error' : usagePercent > 70 ? 'warning' : 'primary'}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}
            
            {limit.updatedAt && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                <HistoryIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled">
                  Last changed: {new Date(limit.updatedAt).toLocaleString()}
                  {limit.updatedBy && ` by ${limit.updatedBy}`}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const FeatureFlags = () => {
  const [flags, setFlags] = useState([]);
  const [limits, setLimits] = useState([]);
  const [betaCapacity, setBetaCapacity] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingKeys, setUpdatingKeys] = useState(new Set());
  
  // Emergency pause dialog
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseLoading, setPauseLoading] = useState(false);

  // Daily summary config
  const [summaryEnabled, setSummaryEnabled] = useState(false);
  const [summaryEmail, setSummaryEmail] = useState('');
  const [summaryHour, setSummaryHour] = useState(6);
  const [summaryLastSent, setSummaryLastSent] = useState(null);
  const [summarySaving, setSummarySaving] = useState(false);
  const [summaryTesting, setSummaryTesting] = useState(false);
  const [summaryLoaded, setSummaryLoaded] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getFeatureFlags();
      setFlags(response.data.flags || []);
      setLimits(response.data.limits || []);
      setBetaCapacity(response.data.betaCapacity || null);
      setUsageStats(response.data.usageStats || null);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
      setError('Failed to load feature flags. ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSummaryConfig = useCallback(async () => {
    try {
      const response = await adminAPI.getDailySummaryConfig();
      const { enabled, email, hour, lastSent } = response.data.config;
      setSummaryEnabled(enabled);
      setSummaryEmail(email);
      setSummaryHour(hour);
      setSummaryLastSent(lastSent);
      setSummaryLoaded(true);
    } catch (err) {
      console.error('Failed to load daily summary config:', err);
    }
  }, []);

  useEffect(() => {
    loadData();
    loadSummaryConfig();
  }, [loadData, loadSummaryConfig]);

  const handleToggle = async (key, newValue) => {
    try {
      setUpdatingKeys(prev => new Set(prev).add(key));
      setError('');
      
      await adminAPI.updateFeatureFlag(key, { value: newValue });
      
      setFlags(prev => prev.map(f => 
        f.key === key ? { ...f, value: newValue, updatedAt: new Date().toISOString() } : f
      ));
      
      setSuccess(`${FLAG_CONFIG[key]?.label || key} has been ${newValue ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Failed to update flag:', err);
      setError(`Failed to update ${key}: ${err.response?.data?.error || err.message}`);
    } finally {
      setUpdatingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleLimitUpdate = async (key, newValue) => {
    try {
      setUpdatingKeys(prev => new Set(prev).add(key));
      setError('');
      
      await adminAPI.updateFeatureFlag(key, { value: newValue });
      
      setLimits(prev => prev.map(l => 
        l.key === key ? { ...l, value: newValue, updatedAt: new Date().toISOString() } : l
      ));
      
      // Refresh beta capacity if user limit changed
      if (key === 'beta_user_limit') {
        const response = await adminAPI.getFeatureFlags();
        setBetaCapacity(response.data.betaCapacity || null);
      }
      
      setSuccess(`${LIMIT_CONFIG[key]?.label || key} has been updated to ${newValue === 0 ? 'unlimited' : newValue}`);
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Failed to update limit:', err);
      setError(`Failed to update ${key}: ${err.response?.data?.error || err.message}`);
    } finally {
      setUpdatingKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleSaveSummaryConfig = async () => {
    try {
      setSummarySaving(true);
      setError('');
      await adminAPI.updateDailySummaryConfig({
        enabled: summaryEnabled,
        email: summaryEmail,
        hour: summaryHour,
      });
      setSuccess('Daily summary configuration saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to save daily summary config:', err);
      setError(`Failed to save daily summary config: ${err.response?.data?.error || err.message}`);
    } finally {
      setSummarySaving(false);
    }
  };

  const handleTestSummary = async () => {
    try {
      setSummaryTesting(true);
      setError('');
      await adminAPI.testDailySummary();
      setSuccess('Test summary email sent successfully — check inbox');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Failed to send test summary:', err);
      setError(`Failed to send test summary: ${err.response?.data?.error || err.message}`);
    } finally {
      setSummaryTesting(false);
    }
  };

  const handlePauseAll = async () => {
    try {
      setPauseLoading(true);
      await adminAPI.pauseAllIngestion({ reason: pauseReason || 'Emergency pause' });
      
      setFlags(prev => prev.map(f => 
        f.key === 'ingestion_enabled' ? { ...f, value: false, updatedAt: new Date().toISOString() } : f
      ));
      
      setPauseDialogOpen(false);
      setPauseReason('');
      setSuccess('All ingestion has been PAUSED');
      setTimeout(() => setSuccess(''), 5000);
      
    } catch (err) {
      console.error('Failed to pause ingestion:', err);
      setError(`Failed to pause: ${err.response?.data?.error || err.message}`);
    } finally {
      setPauseLoading(false);
    }
  };

  const handleResumeAll = async () => {
    try {
      setUpdatingKeys(prev => new Set(prev).add('ingestion_enabled'));
      await adminAPI.resumeAllIngestion();
      
      setFlags(prev => prev.map(f => 
        f.key === 'ingestion_enabled' ? { ...f, value: true, updatedAt: new Date().toISOString() } : f
      ));
      
      setSuccess('All ingestion has been RESUMED');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      console.error('Failed to resume ingestion:', err);
      setError(`Failed to resume: ${err.response?.data?.error || err.message}`);
    } finally {
      setUpdatingKeys(prev => {
        const next = new Set(prev);
        next.delete('ingestion_enabled');
        return next;
      });
    }
  };

  const isIngestionPaused = flags.find(f => f.key === 'ingestion_enabled')?.value === false;

  // Get current usage for limits
  const getUsageForLimit = (key) => {
    if (!usageStats) return undefined;
    if (key === 'global_daily_sms_limit') return usageStats.global?.sms;
    if (key === 'global_daily_voice_limit') return usageStats.global?.voice;
    if (key === 'global_daily_email_limit') return usageStats.global?.email;
    return undefined;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Separate limits into categories
  const betaLimit = limits.find(l => l.key === 'beta_user_limit');
  const perUserLimits = limits.filter(l => l.key.startsWith('user_daily'));
  const globalLimits = limits.filter(l => l.key.startsWith('global_daily'));

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
        Feature Flags & Limits
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: { xs: '0.8rem', sm: '1rem' } }}>
        System-wide kill switches, feature toggles, and usage limits for controlling service availability.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* System Status & Emergency Controls */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 4, bgcolor: isIngestionPaused ? 'error.lighter' : 'success.lighter' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            {isIngestionPaused ? (
              <ToggleOffIcon sx={{ fontSize: 40, color: 'error.main' }} />
            ) : (
              <ToggleOnIcon sx={{ fontSize: 40, color: 'success.main' }} />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                System Status: {isIngestionPaused ? 'PAUSED' : 'RUNNING'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                {isIngestionPaused
                  ? 'All message ingestion is currently paused. No SMS, voice, or email will be processed.'
                  : 'System is accepting and processing messages normally.'
                }
              </Typography>
            </Box>
          </Box>
          <Box>
            {isIngestionPaused ? (
              <Button
                variant="contained"
                color="success"
                startIcon={<PlayIcon />}
                onClick={handleResumeAll}
                disabled={updatingKeys.has('ingestion_enabled')}
              >
                Resume All Ingestion
              </Button>
            ) : (
              <Button
                variant="contained"
                color="error"
                startIcon={<PauseIcon />}
                onClick={() => setPauseDialogOpen(true)}
              >
                Emergency Pause All
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Beta Capacity Card */}
      {betaCapacity && betaLimit && betaLimit.value > 0 && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <PeopleIcon color="secondary" />
            <Typography variant="h6" fontWeight={600}>
              Beta Capacity
            </Typography>
            <Chip 
              label={betaCapacity.allowed ? 'Accepting Signups' : 'Full'} 
              color={betaCapacity.allowed ? 'success' : 'error'}
              size="small"
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Users registered
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {betaCapacity.current} / {betaCapacity.limit}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(betaCapacity.current / betaCapacity.limit) * 100}
              color={betaCapacity.current >= betaCapacity.limit ? 'error' : 
                     betaCapacity.current >= betaCapacity.limit * 0.8 ? 'warning' : 'secondary'}
              sx={{ height: 12, borderRadius: 6 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {betaCapacity.limit - betaCapacity.current} spots remaining. 
            Change the Beta User Cap below to adjust.
          </Typography>
        </Paper>
      )}

      {/* Today's Usage Stats */}
      {usageStats && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TrendingUpIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Today's Usage
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={600} color="primary.main">
                  {usageStats.global?.sms || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">SMS</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={600} color="primary.main">
                  {usageStats.global?.voice || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Voice Calls</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={600} color="primary.main">
                  {usageStats.global?.email || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Emails</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h4" fontWeight={600} color="secondary.main">
                  {usageStats.activeUsersToday || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">Active Users</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Feature Flags Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Channel Controls
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Toggle individual channels and features. Note: If global ingestion is paused, 
          individual channel settings will have no effect until ingestion is resumed.
        </Typography>
        
        <Divider sx={{ mb: 3 }} />

        {flags.length === 0 ? (
          <Alert severity="info">
            No feature flags found. Run the migration to initialize.
          </Alert>
        ) : (
          flags.map(flag => (
            <FlagCard
              key={flag.key}
              flag={flag}
              config={FLAG_CONFIG[flag.key]}
              onToggle={handleToggle}
              loading={updatingKeys.has(flag.key)}
            />
          ))
        )}
      </Paper>

      {/* Limits Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Usage Limits
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Set caps on usage to control costs and prevent abuse. Set to 0 for unlimited.
        </Typography>
        
        <Divider sx={{ mb: 3 }} />

        {/* Beta User Limit */}
        {betaLimit && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, mt: 2 }}>
              Beta Controls
            </Typography>
            <LimitCard
              limit={betaLimit}
              config={LIMIT_CONFIG[betaLimit.key]}
              currentUsage={betaCapacity?.current}
              onUpdate={handleLimitUpdate}
              loading={updatingKeys.has(betaLimit.key)}
            />
          </>
        )}

        {/* Per-User Limits */}
        {perUserLimits.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, mt: 3 }}>
              Per-User Daily Limits
            </Typography>
            {perUserLimits.map(limit => (
              <LimitCard
                key={limit.key}
                limit={limit}
                config={LIMIT_CONFIG[limit.key]}
                onUpdate={handleLimitUpdate}
                loading={updatingKeys.has(limit.key)}
              />
            ))}
          </>
        )}

        {/* Global Limits */}
        {globalLimits.length > 0 && (
          <>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, mt: 3 }}>
              Global Daily Limits (Cost Protection)
            </Typography>
            {globalLimits.map(limit => (
              <LimitCard
                key={limit.key}
                limit={limit}
                config={LIMIT_CONFIG[limit.key]}
                currentUsage={getUsageForLimit(limit.key)}
                onUpdate={handleLimitUpdate}
                loading={updatingKeys.has(limit.key)}
              />
            ))}
          </>
        )}
      </Paper>

      {/* Daily Summary Email */}
      {summaryLoaded && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <SummarizeIcon color="info" />
            <Typography variant="h6" fontWeight={600}>
              Daily Summary Email
            </Typography>
            <Chip
              label={summaryEnabled ? 'Enabled' : 'Disabled'}
              size="small"
              color={summaryEnabled ? 'success' : 'default'}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Send a daily platform activity summary to the admin. Includes usage stats, subscriber counts, MRR, and alerts.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={500}>Enable daily summary</Typography>
                <Switch
                  checked={summaryEnabled}
                  onChange={(e) => setSummaryEnabled(e.target.checked)}
                  color="primary"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Recipient email"
                type="email"
                value={summaryEmail}
                onChange={(e) => setSummaryEmail(e.target.value)}
                placeholder="admin@example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Send time (ET)</InputLabel>
                <Select
                  value={summaryHour}
                  label="Send time (ET)"
                  onChange={(e) => setSummaryHour(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <ScheduleIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  }
                >
                  {Array.from({ length: 24 }, (_, i) => {
                    const label = i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`;
                    return <MenuItem key={i} value={i}>{label} ET</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleSaveSummaryConfig}
              disabled={summarySaving}
              startIcon={summarySaving ? <CircularProgress size={16} /> : null}
            >
              {summarySaving ? 'Saving...' : 'Save Configuration'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleTestSummary}
              disabled={summaryTesting || !summaryEmail}
              startIcon={summaryTesting ? <CircularProgress size={16} /> : <SendIcon />}
            >
              {summaryTesting ? 'Sending...' : 'Send Test Email'}
            </Button>
            {summaryLastSent && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                <HistoryIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.disabled">
                  Last sent: {summaryLastSent}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Emergency Pause Dialog */}
      <Dialog open={pauseDialogOpen} onClose={() => setPauseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningIcon />
          Emergency Pause All Ingestion
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will immediately stop ALL message processing across SMS, voice, and email channels.
            Users will receive "service temporarily unavailable" responses.
          </Alert>
          <TextField
            fullWidth
            label="Reason for pause (optional)"
            placeholder="e.g., Investigating unusual activity, System maintenance"
            value={pauseReason}
            onChange={(e) => setPauseReason(e.target.value)}
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPauseDialogOpen(false)} disabled={pauseLoading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handlePauseAll}
            disabled={pauseLoading}
            startIcon={pauseLoading ? <CircularProgress size={16} /> : <PauseIcon />}
          >
            {pauseLoading ? 'Pausing...' : 'Pause All Ingestion'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeatureFlags;
